/* eslint-disable */
// src/forms/localMachine/createlocalMachineForm.js

import React, { Component } from "react";
import LocalMachineService from "services/localMachineService";
import MachineService from "services/machineService";
import MachineTypeService from "services/machineTypeService";
import FormulaService from "services/formulaService";
import CounterFormulaService from "services/counterFormulaService";
import LocalMachineProfitPercentageService from "services/localMachineProfitPercentageService";
import RentService from "services/rentService";
import VisitTransactionService from "services/visitTransactionService";
import propTypes from "prop-types";
import {
  Button,
  ButtonGroup,
  IconButton,
  TextField,
  Grid,
  Container,
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Box,
  Tooltip,
  InputAdornment,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  CircularProgress,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import esLocale from "date-fns/locale/es";
import CancelButton from "components/common/CancelButton";
import AcceptButton from "components/common/AcceptButton";
import AppFormSection from "components/common/AppFormSection";
import {
  dateOnlyFromDateTime,
  getDefaultOperationDate,
  isBeforeMinOperationDate,
  isFutureOperationDate,
  isValidOperationDate,
  parseDateOnlyToLocalDate,
  toLocalDateOnlyString,
} from "utils/operationDate";
import {
  findCanonicalFormula,
  getFormulaId,
} from "utils/formula";
import AppNumberInput from "components/common/AppNumberInput";

const CUSTOM_COUNTER_FORMULA_OPTION = { Id: "__custom__", Type: "", Value: "", isCustom: true };
const NO_COUNTER_FORMULA_OPTION = { Id: "__none__", Type: "", Value: "", isNone: true };
const selectorFontSize = "0.75rem";
const formPalette = {
  text: "#26374a",
  mutedText: "#52657a",
  accent: "#55799f",
  accentHover: "#3f638a",
  softBorder: "#d0dbe6",
  section: "#f8fafc",
  sectionHeader: "#e2e9f0",
  inset: "#eef3f8",
  insetAccent: "#9aafc4",
  selected: "#dfebf6",
  selectedHover: "#d1e3f3",
};
const quietChoiceSx = (selected) => ({
  m: 0,
  minWidth: 0,
  minHeight: 48,
  px: 1.25,
  border: "1px solid",
  borderColor: selected ? formPalette.accent : formPalette.softBorder,
  borderRadius: 1.5,
  backgroundColor: selected ? formPalette.selected : "#fff",
  color: formPalette.text,
  transition: "border-color 120ms ease, background-color 120ms ease",
  "&:hover": { borderColor: formPalette.accent },
  "&:focus-within": { outline: `2px solid ${formPalette.accent}`, outlineOffset: 2 },
  "& .MuiFormControlLabel-label": { fontSize: "0.875rem", fontWeight: 600 },
  "& .MuiRadio-root": { p: 0.75, mr: 0.25, color: formPalette.mutedText },
  "& .MuiRadio-root.Mui-checked": { color: formPalette.accent },
});
const quietSegmentedButtonSx = {
  "& .MuiButton-root": {
    color: formPalette.text,
    borderColor: formPalette.softBorder,
    backgroundColor: "#fff",
    fontSize: selectorFontSize,
    fontWeight: 700,
    "&:hover": { color: formPalette.text, borderColor: formPalette.accent, backgroundColor: formPalette.section },
    "&.MuiButton-contained": {
      color: formPalette.text,
      borderColor: formPalette.accent,
      backgroundColor: formPalette.selected,
      boxShadow: "none",
      "&:hover": { color: formPalette.text, borderColor: formPalette.accentHover, backgroundColor: formPalette.selectedHover, boxShadow: "none" },
    },
  },
};
const compactChoiceGroupSx = (columns, expanded = false, invalid = false) => ({
  ...quietSegmentedButtonSx,
  display: "grid",
  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  "& .MuiButton-root": {
    ...quietSegmentedButtonSx["& .MuiButton-root"],
    width: "100%",
    minWidth: 0,
    height: 40,
    boxSizing: "border-box",
    px: { xs: 0.25, sm: 0.5 },
    whiteSpace: "nowrap",
  },
  "& .MuiButtonGroup-middleButton, & .MuiButtonGroup-lastButton": { marginLeft: 0 },
  "& .MuiButtonGroup-firstButton, & .MuiButtonGroup-middleButton": { borderRightWidth: 0 },
  ...(expanded && {
    "& .MuiButtonGroup-firstButton": { borderBottomLeftRadius: 0 },
    "& .MuiButtonGroup-lastButton": { borderBottomRightRadius: 0 },
  }),
  ...(invalid && { outline: "2px solid #d32f2f", borderRadius: 1 }),
});
const mainSectionSx = {
  border: `1px solid ${formPalette.softBorder}`,
  borderLeft: `3px solid ${formPalette.accent}`,
  borderRadius: 1.5,
  backgroundColor: formPalette.section,
  p: { xs: 1.5, sm: 2 },
  "& > .MuiTypography-root:first-child": {
    mx: { xs: -1.5, sm: -2 },
    mt: { xs: -1.5, sm: -2 },
    mb: 2,
    px: { xs: 1.5, sm: 2 },
    py: 1,
    borderRadius: "4px 4px 0 0",
    backgroundColor: formPalette.sectionHeader,
    color: formPalette.text,
    fontSize: "1.125rem",
    fontWeight: 700,
    lineHeight: 1.35,
  },
  "& .MuiOutlinedInput-root:not(.Mui-disabled)": { backgroundColor: "#fff" },
  "& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
    borderColor: formPalette.accent,
  },
  "& .MuiInputLabel-root.Mui-focused:not(.Mui-error)": { color: formPalette.accentHover },
  "& .MuiCheckbox-root.Mui-checked, & .MuiRadio-root.Mui-checked": { color: formPalette.accent },
};
const counterSectionSx = {
  border: `1px solid ${formPalette.softBorder}`,
  borderLeft: `2px solid ${formPalette.insetAccent}`,
  borderRadius: 1,
  backgroundColor: formPalette.inset,
  p: 1.5,
  mt: 2.5,
};
const profitAdjustCellSx = {
  gridColumn: "span 2",
  width: "100%",
  minWidth: 0,
  height: "100%",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  border: `1px solid ${formPalette.accent}`,
  backgroundColor: formPalette.selected,
  color: formPalette.text,
  fontFamily: "inherit",
  fontSize: selectorFontSize,
  fontWeight: 700,
  lineHeight: 1,
  textTransform: "uppercase",
};

const getAvailableMachineSerialLabel = (machine) => {
  const serialNumber = String(machine?.SerialNumber || "").trim();
  const normalizedSerialNumber = serialNumber
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  return !serialNumber || normalizedSerialNumber === "sin numero de serie"
    ? "Sin número de serie"
    : serialNumber;
};

const getAvailableMachineLocationLabel = (machine) => {
  const depositName = String(machine?.CurrentDepositLocalName || "").trim();
  return depositName || "Sin depósito detectado";
};

const getAvailableMachineOptionLabel = (machine) => {
  const name = String(machine?.Name || "").trim() || "Sin nombre";
  return `${name} - ${getAvailableMachineSerialLabel(
    machine
  )} - ${getAvailableMachineLocationLabel(machine)}`;
};

class CreateLocalMachineForm extends Component {
  constructor(props) {
    super(props);
    this.machineSelectionRequestId = 0;
    this.isUnmounted = false;
    this.submissionInProgress = false;
    this.state = {
      localMachine: {
        MachineId: "",
        LocalId: props.local ? props.local.Id : "",
        IsAccumulative: false,
        FormulaId: null,
        LocalDisplayNumber: "",
        // RentId se setea dinamicamente si corresponde
      },
      machines: [],
      machineTypes: [],
      formulas: [],
      selectedFormulaId: "",
      formulaMode: "custom",
      counterFormulas: [],
      availableMachineTypes: [],       // tipos que tienen máquinas disponibles
      selectedMachineType: null,       // { Id, Type }
      filteredMachines: [],            // máquinas según tipo elegido
      selectedMachine: null,
      isConfigurationLoading: false,
      isLoading: true,
      isSubmitting: false,

      localMachineProfitPercentage: {
        clientPercentage: "",
        ownerPercentage: "",
      },

      isFirstMachineTypeInLocal: false,

      isRented: false,
      rentData: {
        price: "",
        paymentFrequency: "",
      },

      // datos de la formula que se va a crear
      formulaForm: {
        Type: "",
        Value: "",
      },

      hasExistingFormulaForType: false,
      existingFormulaForType: null,

      showProfitPercentageAdjust: false, 
      selectedProfitPreset: null,
      profitMenuAnchorEl: null,

      existingProfitPercentage: null,
      showFormulaAdjust: false,
      selectedFormulaPreset: null,
      chipValueMenuAnchorEl: null,
      effectiveDate: getDefaultOperationDate(),
      lastLocalCollectionDate: null,
      generalError: "",
      showRequiredErrors: false,
      counterConfig: {
        enabled: false,
        useDifferentFormulas: false,
        sharedFormulaMode: "custom",
        sharedFormulaId: "",
        sharedFormulaType: "",
        sharedFormulaValue: "",
        inFormulaMode: "custom",
        inFormulaId: "",
        inFormulaType: "",
        inFormulaValue: "",
        outFormulaMode: "custom",
        outFormulaId: "",
        outFormulaType: "",
        outFormulaValue: "",
        ToleranceAmount: "10",
      },
      counterConfigError: "",
    };
  }

  async componentDidMount() {
    try {
      const [availableMachinesData, machineTypesData, lastCollection, counterFormulasData, formulasData] = await Promise.all([
        MachineService.getAvailableMachinesWithDetails(),
        MachineTypeService.getMachineTypes(),
        this.props.local?.Id
          ? VisitTransactionService.getLastCollectionByLocal(this.props.local.Id)
          : Promise.resolve(null),
        CounterFormulaService.getCounterFormulas(),
        FormulaService.getFormulas(),
      ]);
      if (this.isUnmounted) return;
      console.log("Máquinas disponibles recibidas:", availableMachinesData);
      console.log("Tipos de máquina recibidos:", machineTypesData);

      const availableTypeIds = new Set(
        (availableMachinesData || [])
          .map((m) => m?.MachineType?.Id)
          .filter(Boolean)
      );

      const availableMachineTypes = (machineTypesData || []).filter((t) =>
        availableTypeIds.has(t.Id)
      );

      const selectedMachine =
        availableMachinesData.find((m) => m.Id === this.state.localMachine.MachineId) || null;
      const lastLocalCollectionDate = parseDateOnlyToLocalDate(
        dateOnlyFromDateTime(lastCollection?.visit?.DateTime)
      );

      this.setState({
        machines: availableMachinesData,
        machineTypes: machineTypesData,
        counterFormulas: counterFormulasData,
        formulas: formulasData,
        counterConfig: this.getEmptyCounterConfig(),
        availableMachineTypes,
        selectedMachine,
        lastLocalCollectionDate,
        isLoading: false,
        formulaForm: { Type: "", Value: "" },
        selectedMachineType: null,
        filteredMachines: [],
      });
    } catch (error) {
      if (this.isUnmounted) return;
      console.error("Error fetching data:", error);
      this.setState({ isLoading: false });
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.machineSelectionRequestId += 1;
  }

  isFormulaType = () => {
    const typeName = this.getSelectedMachineTypeName();
    return ["Tragamoneda", "Pool", "Tejo", "Peluchera"].includes(typeName);
  };

  getSelectedMachineTypeName = () => {
    // 1) prioridad: tipo elegido en dropdown
    const byType = this.state.selectedMachineType?.Type;
    if (byType) return byType;

    // 2) fallback: tipo de la máquina seleccionada
    const selectedMachine = this.state.selectedMachine;
    const type = this.state.machineTypes.find((t) => t.Id === selectedMachine?.MachineType?.Id);
    return type?.Type || "";
  };

  isRentValid = () => {
  if (!this.state.isRented) return true;
  const rawPrice = this.state.rentData.price;
  const price = Number(rawPrice);
  const priceOk = rawPrice !== "" && Number.isFinite(price) && price > 0;
  const freqOk = String(this.state.rentData.paymentFrequency || "").trim() !== "";
  return priceOk && freqOk;
};

// Paso 1 completo: ya eligió máquina y (si es alquiler) completó alquiler
canShowAfterBasics = () => {
  const hasSelectedMachine = Boolean(this.state.selectedMachine && this.state.localMachine.MachineId);
  return hasSelectedMachine && !this.state.isConfigurationLoading && this.isRentValid();
};

  setProfitSplit = (clientValue) => {
    // Mantener vacío hasta que se seleccione un porcentaje.
    if (clientValue === "" || clientValue === null || clientValue === undefined) {
      this.setState({
        localMachineProfitPercentage: { clientPercentage: "", ownerPercentage: "" },
        selectedProfitPreset: null,
      });
      return;
    }

    const client = Number(clientValue);
    if (!Number.isInteger(client) || client < 0 || client > 100 || client % 10 !== 0) return;
    const owner = 100 - client;

    // Si lo ajusta manual, ya no hay "preset" seleccionado
    this.setState({
      localMachineProfitPercentage: {
        clientPercentage: String(client),
        ownerPercentage: String(owner),
      },
      selectedProfitPreset: null,
    });
  };

  handleProfitMenuSelect = (clientValue) => {
    this.setProfitSplit(clientValue);
    this.setState({ profitMenuAnchorEl: null });
  };

  handleMachineTypeChange = (typeObj) => {
    this.machineSelectionRequestId += 1;
    const machines = this.state.machines || [];

    const filteredMachines = typeObj
      ? machines.filter((m) => Number(m?.MachineType?.Id) === Number(typeObj.Id))
      : [];

    // Reseteamos selección de máquina y estados dependientes
    this.setState({
      selectedMachineType: typeObj,
      filteredMachines,

      // limpiar máquina seleccionada (clave)
      selectedMachine: null,
      isConfigurationLoading: false,
      isRented: false,
      rentData: { price: "", paymentFrequency: "" },
      showRequiredErrors: false,
      generalError: "",
      localMachine: {
        ...this.state.localMachine,
        MachineId: "",
        FormulaId: null,
        IsAccumulative: false,
        LocalDisplayNumber: "",
      },

      // reset de lógicas que dependen de máquina/tipo
      isFirstMachineTypeInLocal: false,
      hasExistingFormulaForType: false,
      existingFormulaForType: null,
      formulaForm: { Type: "", Value: "" },
      selectedFormulaId: "",
      formulaMode: "custom",

      localMachineProfitPercentage: { clientPercentage: "", ownerPercentage: "" },
      showProfitPercentageAdjust: false,
      selectedProfitPreset: null,
      profitMenuAnchorEl: null,
      existingProfitPercentage: null,
      showFormulaAdjust: false,
      selectedFormulaPreset: null,
      chipValueMenuAnchorEl: null,
      ...this.resetCounterConfig(),
    });
  };


  // Presets tipo Splitwise (un click) 
  handlePresetSplit = (clientPct, presetKey) => {
    const ownerPct = 100 - Number(clientPct);
    this.setState({
      localMachineProfitPercentage: {
        clientPercentage: String(clientPct),
        ownerPercentage: String(ownerPct),
      },
      selectedProfitPreset: presetKey,
      showProfitPercentageAdjust: false,
      profitMenuAnchorEl: null,
    });
  };

  handlePresetFormulaTragamoneda = (symbol, value) => {
  this.setState({
    formulaForm: { Type: symbol, Value: String(value) },
    selectedFormulaId: "",
    formulaMode: "custom",
    selectedFormulaPreset: `${symbol}${value}`,
    showFormulaAdjust: false,
  });
};

handlePresetNoFormula = () => {
  this.setState({
    formulaForm: { Type: "", Value: "" },
    selectedFormulaId: "",
    formulaMode: "custom",
    selectedFormulaPreset: "no-formula",
    showFormulaAdjust: false,
  });
};

handlePresetChipValue = (value) => {
  // Pool/Tejo/Peluchera: siempre Type="*"
  this.setState({
    formulaForm: { Type: "*", Value: String(value) },
    selectedFormulaId: "",
    formulaMode: "custom",
    selectedFormulaPreset: `chip${value}`,
    showFormulaAdjust: false,
    chipValueMenuAnchorEl: null,
  });
};

handleChipValueMenuSelect = (value) => {
  this.setState({
    formulaForm: { Type: "*", Value: String(value) },
    selectedFormulaId: "",
    formulaMode: "custom",
    selectedFormulaPreset: null,
    chipValueMenuAnchorEl: null,
  });
};

handleToggleFormulaAdjust = () => {
  this.setState((prev) => {
    const isOpening = !prev.showFormulaAdjust;
    const typeName = this.getSelectedMachineTypeName();
    const isChipType = ["Pool", "Tejo", "Peluchera"].includes(typeName);
    const isTragaType = typeName === "Tragamoneda";

    return {
      showFormulaAdjust: isOpening,
      formulaMode: isOpening ? "custom" : prev.formulaMode,
      selectedFormulaId: isOpening ? "" : prev.selectedFormulaId,
      selectedFormulaPreset: isOpening ? null : prev.selectedFormulaPreset,
      chipValueMenuAnchorEl: null,

      formulaForm: isOpening
        ? isTragaType
          ? { Type: "/", Value: "" }
          : { ...prev.formulaForm, Type: isChipType ? "*" : prev.formulaForm.Type, Value: "" }
        : isChipType || isTragaType
          ? { Type: "", Value: "" }
          : prev.formulaForm,
    };
  });
};


  handleMachineChange = async (value) => {
    const requestId = ++this.machineSelectionRequestId;
    console.log("Máquina seleccionada:", value);

    const localId = this.state.localMachine.LocalId;
    const typeName = value?.MachineType?.Type || "";
    const nextIsAccumulative = typeName === "Tragamoneda";

    this.setState({
      localMachine: {
        ...this.state.localMachine,
        MachineId: value ? value.Id : "",
        FormulaId: null, // se decide despues
        IsAccumulative: Boolean(nextIsAccumulative),
      },
      selectedMachine: value,
      isConfigurationLoading: Boolean(value),
      isRented: false,
      rentData: { price: "", paymentFrequency: "" },
      showRequiredErrors: false,
      generalError: "",
      selectedFormula: null,
      isFirstMachineTypeInLocal: false,
      hasExistingFormulaForType: false,
      existingFormulaForType: null,
      formulaForm: { Type: "", Value: "" },
      selectedFormulaId: "",
      formulaMode: "custom",

      // Reset % cuando se cambia la máquina/tipo (evita que queden valores viejos)
      localMachineProfitPercentage: { clientPercentage: "", ownerPercentage: "" },
      showProfitPercentageAdjust: false,
      selectedProfitPreset: null,
      profitMenuAnchorEl: null,
      existingProfitPercentage: null,
      showFormulaAdjust: false,
      selectedFormulaPreset: null,
      chipValueMenuAnchorEl: null,
      ...this.resetCounterConfig(),
    });

    if (!value) return;

    const typeId = value?.MachineType?.Id;

    // 1) Ver si es la primera vez que se configura porcentaje de ganancias
    try {
      const profitPercentage =
        await LocalMachineProfitPercentageService.getLocalMachineProfitPercentageByLocalAndType(
          localId,
          typeId
        );
      if (requestId !== this.machineSelectionRequestId) return;

      const isFirst = !profitPercentage;

      this.setState({
        isFirstMachineTypeInLocal: isFirst,
        existingProfitPercentage: profitPercentage
          ? {
              ClientPercentage: profitPercentage.ClientPercentage,
              OwnerPercentage: profitPercentage.OwnerPercentage,
            }
          : null,
      });

      // default UX: 50/50 si es primera vez y no está alquilada
      if (isFirst) {
        this.handlePresetSplit(50, "50/50");
      }
    } catch (error) {
      if (requestId !== this.machineSelectionRequestId) return;
      console.error("Error verificando el tipo de máquina en el local (profit percentage):", error);
    }

    // 2) Para Pool/Tejo/Peluchera: buscar si YA hay una LocalMachine de ese tipo
    //    y, si es así, reutilizar su fórmula.
    if (["Pool", "Tejo", "Peluchera"].includes(typeName)) {
      try {
        const localMachines = await LocalMachineService.getActiveLocalMachineByLocal(localId);
        if (requestId !== this.machineSelectionRequestId) return;

        console.log("[CreateLocalMachine] LocalMachines en el local:", localMachines);

        const sameType = (localMachines || []).filter(
          (lm) => Number(lm.MachineTypeId) === Number(typeId) && !lm.RentId // solo máquinas NO alquiladas
        );

        const firstWithFormula = sameType.find((lm) => lm.FormulaId != null);

        if (firstWithFormula) {
          const canonicalFormula = findCanonicalFormula(
            this.state.formulas,
            firstWithFormula
          );
          console.log("[CreateLocalMachine] Reutilizando formula de tipo", typeName, firstWithFormula);

          this.setState({
            hasExistingFormulaForType: true,
            existingFormulaForType: {
              Id: firstWithFormula.FormulaId,
              Type: firstWithFormula.FormulaType,
              Value: firstWithFormula.FormulaValue,
            },
            selectedFormulaId: getFormulaId(canonicalFormula) ?? firstWithFormula.FormulaId,
            formulaMode: "existing",
            formulaForm: {
              Type: firstWithFormula.FormulaType,
              Value: firstWithFormula.FormulaValue,
            },
          });
        } else {
          this.setState({
            hasExistingFormulaForType: false,
            existingFormulaForType: null,
          });
        }
      } catch (error) {
        if (requestId !== this.machineSelectionRequestId) return;
        console.error("Error buscando LocalMachines para reutilizar formula:", error);
        this.setState({
          hasExistingFormulaForType: false,
          existingFormulaForType: null,
        });
      }
    } else {
      // Otros tipos (Rockola, Tragamoneda, etc.): no reutilizamos formula aqui
      this.setState({
        hasExistingFormulaForType: false,
        existingFormulaForType: null,
      });
    }
    if (requestId === this.machineSelectionRequestId) {
      this.setState({ isConfigurationLoading: false });
    }
  };

  // Edita SOLO clientPercentage y ownerPercentage se calcula como (100 - client)
  handleProfitPercentageChange = (event) => {
    const { value } = event.target;
    this.setProfitSplit(value);
  };

  // Maneja cambios en los campos del formulario
  handleChange = (event) => {
    const { name, value } = event.target;
    this.setState((prevState) => ({
      localMachine: {
        ...prevState.localMachine,
        [name]: value,
      },
    }));
  };

  handleRentDataChange = (event) => {
    const { name, value } = event.target;
    this.setState((prevState) => ({
      rentData: {
        ...prevState.rentData,
        [name]: value,
      },
    }));
  };

  normalizeLocalType = (type) =>
    String(type || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

  isComercioLocal = () => this.normalizeLocalType(this.props.local?.Type) === "comercio";

  getEmptyCounterConfig = () => {
    return {
      enabled: false,
      useDifferentFormulas: false,
      sharedFormulaMode: "existing",
      sharedFormulaId: "",
      sharedFormulaType: "",
      sharedFormulaValue: "",
      inFormulaMode: "custom",
      inFormulaId: "",
      inFormulaType: "",
      inFormulaValue: "",
      outFormulaMode: "custom",
      outFormulaId: "",
      outFormulaType: "",
      outFormulaValue: "",
      ToleranceAmount: "10",
    };
  };

  canShowCounterConfig = () =>
    this.isComercioLocal() &&
    this.getSelectedMachineTypeName() === "Tragamoneda" &&
    !this.state.isRented;

  canShowLocalDisplayNumber = () =>
    this.isComercioLocal() &&
    this.getSelectedMachineTypeName() === "Tragamoneda" &&
    !this.state.isRented;

  resetCounterConfig = () => ({
    counterConfig: this.getEmptyCounterConfig(),
    counterConfigError: "",
  });

  handleCounterConfigEnabledChange = (checked) => {
    if (!this.canShowCounterConfig()) {
      this.setState(this.resetCounterConfig());
      return;
    }

    this.setState((prev) => {
      const defaultConfig = this.getEmptyCounterConfig();

      return {
        counterConfig: checked
          ? {
              ...defaultConfig,
              enabled: true,
              ToleranceAmount:
                prev.counterConfig.ToleranceAmount === "" ||
                prev.counterConfig.ToleranceAmount === null ||
                prev.counterConfig.ToleranceAmount === undefined
                  ? "10"
                  : prev.counterConfig.ToleranceAmount,
            }
          : defaultConfig,
        counterConfigError: "",
        generalError: "",
      };
    });
  };

  handleCounterConfigChange = (name, value) => {
    this.setState((prev) => ({
      counterConfig: {
        ...prev.counterConfig,
        [name]: value,
      },
      counterConfigError: "",
      generalError: "",
    }));
  };

  getCounterFormulaKeys = (scope) => {
    if (scope === "in") {
      return {
        mode: "inFormulaMode",
        id: "inFormulaId",
        type: "inFormulaType",
        value: "inFormulaValue",
      };
    }
    if (scope === "out") {
      return {
        mode: "outFormulaMode",
        id: "outFormulaId",
        type: "outFormulaType",
        value: "outFormulaValue",
      };
    }
    return {
      mode: "sharedFormulaMode",
      id: "sharedFormulaId",
      type: "sharedFormulaType",
      value: "sharedFormulaValue",
    };
  };

  getCounterFormulaOptions = () => [
    ...(this.state.counterFormulas || []),
    NO_COUNTER_FORMULA_OPTION,
    CUSTOM_COUNTER_FORMULA_OPTION,
  ];

  getCounterFormulaOption = (scope) => {
    const keys = this.getCounterFormulaKeys(scope);
    const config = this.state.counterConfig;

    if (config[keys.mode] === "custom") return CUSTOM_COUNTER_FORMULA_OPTION;
    if (config[keys.mode] === "none") return NO_COUNTER_FORMULA_OPTION;
    if (config[keys.id] === "" || config[keys.id] === null || config[keys.id] === undefined) return null;

    return (
      (this.state.counterFormulas || []).find((formula) => Number(formula.Id) === Number(config[keys.id])) ||
      null
    );
  };

  handleCounterFormulaOptionChange = (scope, option) => {
    const keys = this.getCounterFormulaKeys(scope);
    const isCleared = option === null;
    const isNone = option?.isNone === true;
    const isCustom = option?.isCustom === true;

    this.setState((prev) => ({
      counterConfig: {
        ...prev.counterConfig,
        [keys.mode]: isNone ? "none" : isCustom ? "custom" : "existing",
        [keys.id]: isCleared || isNone || isCustom ? "" : option.Id,
        [keys.type]: isCleared || isNone ? "" : isCustom ? prev.counterConfig[keys.type] : option.Type,
        [keys.value]: isCleared || isNone ? "" : isCustom ? prev.counterConfig[keys.value] : String(option.Value),
      },
      counterConfigError: "",
      generalError: "",
    }));
  };

  handleCounterUseDifferentFormulasChange = (checked) => {
    this.setState((prev) => {
      if (prev.counterConfig.useDifferentFormulas === checked) return null;
      if (!checked) {
        return {
          counterConfig: {
            ...prev.counterConfig,
            useDifferentFormulas: false,
          },
          counterConfigError: "",
          generalError: "",
        };
      }

      return {
        counterConfig: {
          ...prev.counterConfig,
          useDifferentFormulas: true,
          inFormulaMode: "existing",
          inFormulaId: "",
          inFormulaType: "",
          inFormulaValue: "",
          outFormulaMode: "existing",
          outFormulaId: "",
          outFormulaType: "",
          outFormulaValue: "",
        },
        counterConfigError: "",
        generalError: "",
      };
    });
  };

  buildCounterFormulaFromConfig = (scope, label) => {
    const keys = this.getCounterFormulaKeys(scope);
    const config = this.state.counterConfig;
    const mode = config[keys.mode];
    if (mode === "none") return { formula: null };
    const selectedFormula =
      mode === "existing" &&
      config[keys.id] !== "" &&
      config[keys.id] !== null &&
      config[keys.id] !== undefined
        ? (this.state.counterFormulas || []).find((formula) => Number(formula.Id) === Number(config[keys.id]))
        : null;
    const type = mode === "existing" ? selectedFormula?.Type : config[keys.type];
    const rawValue = mode === "existing" ? selectedFormula?.Value : config[keys.value];

    if (!type) return { error: `Selecciona la fórmula de contador ${label}.` };
    if (!["*", "/"].includes(type)) return { error: `Selecciona un tipo válido para contador ${label}.` };
    if (rawValue === "" || rawValue === null || rawValue === undefined) {
      return { error: `Ingresa el valor de la fórmula de contador ${label}.` };
    }

    const value = Number(rawValue);
    if (!Number.isInteger(value) || value <= 0) {
      return { error: `El valor de la fórmula de contador ${label} debe ser un entero mayor a 0.` };
    }

    return {
      formula: {
        Type: type,
        Value: value,
      },
    };
  };

  validateCounterConfig = () => {
    if (!this.canShowCounterConfig() || !this.state.counterConfig.enabled) return "";

    const { useDifferentFormulas, ToleranceAmount } = this.state.counterConfig;
    const formulaResults = useDifferentFormulas
      ? [
          this.buildCounterFormulaFromConfig("in", "de entrada"),
          this.buildCounterFormulaFromConfig("out", "de salida"),
        ]
      : [this.buildCounterFormulaFromConfig("shared", "")];

    const formulaError = formulaResults.find((result) => result.error)?.error;
    if (formulaError) {
      return formulaError.replace(" contador .", " contador.");
    }

    if (ToleranceAmount === "" || ToleranceAmount === null || ToleranceAmount === undefined) {
      return "Ingresa la tolerancia permitida de los contadores.";
    }

    const toleranceAmount = Number(ToleranceAmount);

    if (!Number.isFinite(toleranceAmount) || toleranceAmount < 0) {
      return "La tolerancia debe ser un número mayor o igual a 0.";
    }

    return "";
  };

  buildCounterConfigPayload = () => {
    const { useDifferentFormulas, ToleranceAmount } = this.state.counterConfig;
    const sharedFormula = !useDifferentFormulas
      ? this.buildCounterFormulaFromConfig("shared", "").formula
      : null;
    const counterInFormula = useDifferentFormulas
      ? this.buildCounterFormulaFromConfig("in", "de entrada").formula
      : sharedFormula;
    const counterOutFormula = useDifferentFormulas
      ? this.buildCounterFormulaFromConfig("out", "de salida").formula
      : sharedFormula;

    return {
      CounterInFormula: counterInFormula,
      CounterOutFormula: counterOutFormula,
      ToleranceAmount: Number(ToleranceAmount),
    };
  };

  renderCounterFormulaFields = (scope, label) => {
    const keys = this.getCounterFormulaKeys(scope);
    const config = this.state.counterConfig;
    const selectedOption = this.getCounterFormulaOption(scope);
    const isCustom = config[keys.mode] === "custom";
    const showErrors = this.state.showRequiredErrors;
    const optionError = showErrors && config[keys.mode] === "existing" && !selectedOption;
    const typeError = showErrors && isCustom && !["*", "/"].includes(config[keys.type]);
    const valueError = showErrors && isCustom && (!Number.isInteger(Number(config[keys.value])) || Number(config[keys.value]) <= 0);

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Autocomplete
            options={this.getCounterFormulaOptions()}
            value={selectedOption}
            onChange={(event, option) => this.handleCounterFormulaOptionChange(scope, option)}
            getOptionLabel={(option) =>
              option?.isNone
                ? "Sin fórmula"
                : option?.isCustom
                  ? "Ingresar otra fórmula"
                  : `${option?.Type || ""} ${option?.Value || ""}`.trim()
            }
            isOptionEqualToValue={(option, value) =>
              option?.isNone && value?.isNone
                ? true
                : option?.isCustom && value?.isCustom
                ? true
                : Number(option?.Id) === Number(value?.Id)
            }
            renderInput={(params) => <TextField {...params} label={label} required fullWidth error={optionError} helperText={optionError ? "Seleccioná una fórmula." : ""} />}
          />
        </Grid>

        {isCustom && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Tipo fórmula"
                required
                value={config[keys.type]}
                onChange={(e) => this.handleCounterConfigChange(keys.type, e.target.value)}
                error={typeError}
                helperText={typeError ? "Seleccioná un tipo de fórmula." : ""}
              >
                <MenuItem value="*">*</MenuItem>
                <MenuItem value="/">/</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <AppNumberInput
                fullWidth
                label="Valor fórmula"
                required
                value={config[keys.value]}
                onValueChange={(value) =>
                  this.handleCounterConfigChange(keys.value, value)
                }
                decimalScale={0}
                allowNegative={false}
                min={1}
                error={valueError}
                helperText={valueError ? "Ingresá un valor mayor que cero." : ""}
              />
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  getLocalDisplayNumberErrorMessage = () => {
    if (!this.canShowLocalDisplayNumber()) return "";
    const raw = this.state.localMachine.LocalDisplayNumber;
    if (raw === undefined || raw === null || String(raw).trim() === "") return "";
    if (!/^\d+$/.test(String(raw).trim())) {
      return "El Nº en local debe ser un número entero positivo.";
    }
    const n = Number(raw);
    if (!Number.isInteger(n) || n <= 0) {
      return "El Nº en local debe ser un número entero positivo.";
    }
    return "";
  };

  handleEffectiveDateChange = (date) => {
    this.setState({ effectiveDate: date, generalError: "" });
  };

  getMinEffectiveDate = () => {
    const startDateTime = this.state.selectedMachine?.CurrentLocalMachineStartDateTime;
    const machineMinDate = parseDateOnlyToLocalDate(dateOnlyFromDateTime(startDateTime));
    const localCreatedAtDate = parseDateOnlyToLocalDate(
      dateOnlyFromDateTime(this.props.local?.CreatedAt)
    );
    const lastLocalCollectionDate = this.state.lastLocalCollectionDate;

    return [machineMinDate, localCreatedAtDate, lastLocalCollectionDate]
      .filter(Boolean)
      .reduce((latest, current) => {
        if (!latest) return current;
        return current.getTime() >= latest.getTime() ? current : latest;
      }, null);
  };

  getEffectiveDateErrorMessage = () => {
    const { effectiveDate } = this.state;
    const minDate = this.getMinEffectiveDate();
    const { lastLocalCollectionDate } = this.state;

    if (!isValidOperationDate(effectiveDate)) {
      return "Selecciona una fecha válida.";
    }
    if (isFutureOperationDate(effectiveDate)) {
      return "No se puede seleccionar una fecha futura.";
    }
    if (minDate && isBeforeMinOperationDate(effectiveDate, minDate)) {
      if (
        lastLocalCollectionDate &&
        minDate.getTime() === lastLocalCollectionDate.getTime()
      ) {
        return "La fecha seleccionada no puede ser anterior a la última recaudación completa del local.";
      }
      return "La fecha seleccionada no puede ser anterior al último movimiento de la máquina ni a la fecha de creación del local.";
    }
    return "";
  };

  getApiErrorMessage = (error, fallback) => {
    const status = error?.response?.status;
    const apiError = error?.response?.data?.error || fallback;
    const apiCode = error?.response?.data?.code || "";

    if (status === 409 && apiCode === "EFFECTIVE_DATE_BEFORE_MACHINE_CREATION") {
      return "La fecha seleccionada no puede ser anterior a la fecha de creación de la máquina.";
    }
    if (status === 409 && apiCode === "EFFECTIVE_DATE_BEFORE_CURRENT_LOCAL_MACHINE_START") {
      return "La fecha seleccionada no puede ser anterior al último movimiento de la máquina.";
    }
    if (status === 409 && apiCode === "EFFECTIVE_DATE_BEFORE_LOCAL_CREATION") {
      return "La fecha seleccionada no puede ser anterior al último movimiento de la máquina ni a la fecha de creación del local.";
    }
    if (status === 400 && apiCode === "EFFECTIVE_DATE_CANNOT_BE_FUTURE") {
      return "No se puede seleccionar una fecha futura.";
    }
    if (status === 400 && apiCode === "LOCAL_DISPLAY_NUMBER_INVALID") {
      return "El Nº en local debe ser un número entero positivo.";
    }
    if (status === 409 && apiCode === "LOCAL_DISPLAY_NUMBER_DUPLICATE") {
      return "Ya existe una máquina activa con ese Nº en este local.";
    }
    if (status === 400 && apiCode === "LOCAL_DISPLAY_NUMBER_ONLY_FOR_COMERCIO") {
      return "El Nº en local solo aplica a locales comerciales.";
    }

    return apiError || fallback;
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    if (this.submissionInProgress || this.state.isLoading || this.state.isConfigurationLoading) return;
    const { isRented, rentData, formulaForm, effectiveDate } = this.state;
    this.setState({ showRequiredErrors: true, generalError: "" });
    if (!this.state.selectedMachineType || !this.state.selectedMachine) return;
    if (isRented && !this.isRentValid()) return;
    if (
      !isRented &&
      this.getSelectedMachineTypeName() === "Tragamoneda" &&
      !this.state.selectedFormulaPreset &&
      !this.state.showFormulaAdjust
    ) return;
    if (!isRented && this.state.showFormulaAdjust && (!formulaForm.Type || !formulaForm.Value)) return;

    const effectiveDateError = this.getEffectiveDateErrorMessage();
    if (effectiveDateError) {
      return;
    }
    const localDisplayNumberError = this.getLocalDisplayNumberErrorMessage();
    if (localDisplayNumberError) {
      return;
    }
    const counterConfigError = this.validateCounterConfig();
    if (counterConfigError) {
      this.setState({ counterConfigError: "", generalError: "" });
      return;
    }

    this.submissionInProgress = true;
    this.setState({ isSubmitting: true });

    try {
    const { MachineId, LocalId, IsAccumulative } = this.state.localMachine;
    const effectiveDateValue = toLocalDateOnlyString(effectiveDate);

    // payload base para LocalMachine
    const payload = {
      MachineId: Number(MachineId),
      LocalId: Number(LocalId),
      IsAccumulative: !isRented && Boolean(IsAccumulative),
      FormulaId: null, // la seteamos abajo si corresponde
      EffectiveDate: effectiveDateValue,
    };

    if (this.isComercioLocal()) {
      const rawLocalDisplayNumber = this.state.localMachine.LocalDisplayNumber;
      payload.LocalDisplayNumber =
        !this.canShowLocalDisplayNumber() ||
        rawLocalDisplayNumber === undefined ||
        rawLocalDisplayNumber === null ||
        String(rawLocalDisplayNumber).trim() === ""
          ? null
          : Number(rawLocalDisplayNumber);
    }

    const selectedType = this.state.machineTypes.find(
      (t) => t.Id === this.state.selectedMachine?.MachineType?.Id
    );

    // si NO es Tragamoneda, IsAccumulative siempre false
    if (selectedType?.Type !== "Tragamoneda") {
      payload.IsAccumulative = false;
    }

    // 1) Si está alquilada -> crear Rent y NO usar fórmula
    if (isRented) {
      const rentPrice = Number(rentData.price);

      if (!Number.isFinite(rentPrice) || rentPrice <= 0) {
        return;
      }

      const createdRent = await RentService.createRent({
        Price: parseFloat(rentData.price),
        PaymentFrequency: rentData.paymentFrequency,
      });
      if (createdRent?.Id) {
        payload.RentId = Number(createdRent.Id);
      }
      payload.FormulaId = null; // alquiladas no usan formula
    } else {
      const typeName = selectedType?.Type;
      const isFormulaType = ["Tragamoneda", "Pool", "Tejo", "Peluchera"].includes(typeName);
      if (!isFormulaType) {
        payload.FormulaId = null;
      } else {
        const hasFormulaData =
          (formulaForm.Type && String(formulaForm.Type).trim() !== "") ||
          (formulaForm.Value && String(formulaForm.Value).trim() !== "");
        const selectedFormulaId = Number(this.state.selectedFormulaId);

        if (this.state.formulaMode === "existing" && Number.isInteger(selectedFormulaId)) {
          payload.FormulaId = selectedFormulaId;
        } else if (hasFormulaData) {
          payload.FormulaId = null;
          payload.Formula = {
            Type: formulaForm.Type,
            Value: formulaForm.Value,
          };
        } else {
          payload.FormulaId = null;
        }
      }
    }

    console.log("Datos enviados al backend:", payload);

      // 3) Si es la primera vez y NO está alquilada, crear porcentaje de ganancias
      if (!isRented && this.state.isFirstMachineTypeInLocal) {
        const clientPct = Number(this.state.localMachineProfitPercentage.clientPercentage);
        const ownerPct = Number(this.state.localMachineProfitPercentage.ownerPercentage);
        if (
          Number.isNaN(clientPct) ||
          Number.isNaN(ownerPct) ||
          this.state.localMachineProfitPercentage.clientPercentage === "" ||
          this.state.localMachineProfitPercentage.ownerPercentage === "" ||
          !Number.isInteger(clientPct) ||
          !Number.isInteger(ownerPct) ||
          clientPct < 0 ||
          clientPct > 100 ||
          clientPct % 10 !== 0 ||
          ownerPct < 0 ||
          ownerPct > 100 ||
          clientPct + ownerPct !== 100
        ) {
          console.error("[CreateLocalMachine] Porcentajes inválidos. Deben sumar 100.", {
            clientPct,
            ownerPct,
          });
          if (clientPct !== 100 - ownerPct && !Number.isNaN(clientPct) && !Number.isNaN(ownerPct)) {
            this.setState({ generalError: "Los porcentajes del cliente y del dueño deben sumar 100." });
          }
          return;
        }

        await LocalMachineProfitPercentageService.createLocalMachineProfitPercentage({
          LocalId: this.props.local.Id,
          MachineTypeId: this.state.selectedMachine.MachineType.Id,
          ClientPercentage: clientPct,
          OwnerPercentage: ownerPct,
          EffectiveDate: payload.EffectiveDate,
        });
      }

      let createdLocalMachine;
      if (this.canShowCounterConfig() && this.state.counterConfig.enabled) {
        createdLocalMachine = await LocalMachineService.createLocalMachineWithCounterConfig({
          ...payload,
          CounterConfig: this.buildCounterConfigPayload(),
        });
      } else {
        createdLocalMachine = await LocalMachineService.createLocalMachine(payload);
      }
      console.log("Máquina agregada al local:", createdLocalMachine);

      this.props.onCreateSuccess?.();
      this.props.closeForm();
    } catch (error) {
      console.error("Hubo un error al crear la máquina del local:", error);
      if (!this.isUnmounted) {
        this.setState({
          generalError: this.getApiErrorMessage(
            error,
            "No se pudo agregar la máquina al local. Intenta nuevamente."
          ),
        });
      }
    } finally {
      this.submissionInProgress = false;
      if (!this.isUnmounted) this.setState({ isSubmitting: false });
    }
  };

  handleToggleRented = (checked) => {
    if (this.state.isRented === checked) return;
    this.setState({
      isRented: checked,
      chipValueMenuAnchorEl: null,
      showRequiredErrors: false,
      generalError: "",
    });
  };

handleCancel = () => {
  this.props.closeForm();
};

  render() {
    const selectedTypeName = this.getSelectedMachineTypeName();

    const canShowAfterBasics = this.canShowAfterBasics();

    const isFormulaType = ["Tragamoneda", "Pool", "Tejo", "Peluchera"].includes(selectedTypeName);

    // Mostrar sector formula SOLO si:
    // - ya completó selección + alquiler
    // - NO está alquilada
    // - y el tipo usa fórmula
    const showFormulaSection = canShowAfterBasics && !this.state.isRented && isFormulaType;
    const isChipType = ["Pool", "Tejo", "Peluchera"].includes(selectedTypeName);
    const hasExistingChipValue =
      isChipType &&
      this.state.hasExistingFormulaForType &&
      this.state.existingFormulaForType &&
      this.state.formulaMode === "existing";
    const chipValueError =
      this.state.showRequiredErrors &&
      this.state.showFormulaAdjust &&
      !this.state.formulaForm.Value;

    // Mostrar sector ganancias SOLO si:
    // - ya completó selección + alquiler
    // - y NO está alquilada (alquiladas no usan ProfitPercentage en tu lógica)
    const showProfitSection = canShowAfterBasics && !this.state.isRented;
    const showCounterSection = canShowAfterBasics && this.canShowCounterConfig();

    // Si ya existe % en el local: isFirstMachineTypeInLocal = false
    const hasExistingProfitConfigured =
      showProfitSection && !this.state.isFirstMachineTypeInLocal && this.state.existingProfitPercentage;
    const isCustomProfitSelected =
      this.state.showProfitPercentageAdjust ||
      (!this.state.selectedProfitPreset &&
        this.state.localMachineProfitPercentage.clientPercentage !== "");
    const profitClientError =
      this.state.showRequiredErrors &&
      this.state.localMachineProfitPercentage.clientPercentage === "";

    const { effectiveDate, generalError } = this.state;
    const minEffectiveDate = this.getMinEffectiveDate();
    const effectiveDateError = this.getEffectiveDateErrorMessage();
    const localDisplayNumberError = this.getLocalDisplayNumberErrorMessage();
    const today = getDefaultOperationDate();
    return (
      <Paper sx={{ width: "100%", display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
        <form
          onSubmit={this.handleSubmit}
          noValidate
          aria-busy={this.state.isSubmitting}
          style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }}
        >
          <Box
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 1,
              pb: 2,
            }}
          >
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <AppFormSection title="Selección de máquina" sx={mainSectionSx}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={this.state.availableMachineTypes}
                      getOptionLabel={(option) => option?.Type || ""}
                      value={this.state.selectedMachineType}
                      onChange={(event, value) => this.handleMachineTypeChange(value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Tipo"
                          required
                          placeholder="Selecciona un tipo"
                          fullWidth
                          error={this.state.showRequiredErrors && !this.state.selectedMachineType}
                          helperText={this.state.showRequiredErrors && !this.state.selectedMachineType ? "Seleccioná un tipo de máquina." : ""}
                          sx={{
                            "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                              lineHeight: 1.25,
                              transform: "translate(14px, 19px) scale(1)",
                            },
                          }}
                        />
                      )}
                      disabled={this.state.isLoading}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={this.state.filteredMachines}
                      getOptionLabel={getAvailableMachineOptionLabel}
                      ListboxProps={{
                        sx: {
                          display: "grid",
                          gridTemplateColumns: "max-content max-content max-content",
                          columnGap: 1.5,
                          width: "max-content",
                          maxWidth: "100%",
                          overflowX: "auto",
                          "& .MuiAutocomplete-option": {
                            gridColumn: "1 / -1",
                            display: "grid !important",
                            gridTemplateColumns: "subgrid",
                          },
                          "& .MuiAutocomplete-option > span": { whiteSpace: "nowrap" },
                        },
                      }}
                      slotProps={{
                        popper: {
                          sx: {
                            width: "max-content !important",
                            maxWidth: "90vw",
                          },
                        },
                      }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ color: formPalette.text }}>
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {String(option?.Name || "").trim() || "Sin nombre"}
                          </Box>
                          <Box component="span">{getAvailableMachineSerialLabel(option)}</Box>
                          <Box component="span">
                            {getAvailableMachineLocationLabel(option)}
                          </Box>
                        </Box>
                      )}
                      value={this.state.selectedMachine}
                      onChange={(event, value) => this.handleMachineChange(value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Máquina"
                          required
                          placeholder={
                            this.state.selectedMachineType
                              ? "Selecciona una máquina"
                              : "Primero selecciona un tipo"
                          }
                          fullWidth
                          error={this.state.showRequiredErrors && Boolean(this.state.selectedMachineType) && !this.state.selectedMachine}
                          helperText={this.state.showRequiredErrors && this.state.selectedMachineType && !this.state.selectedMachine ? "Seleccioná una máquina." : ""}
                          sx={{
                            "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                              lineHeight: 1.25,
                              transform: "translate(14px, 19px) scale(1)",
                            },
                            "& .MuiInputBase-root": {
                              backgroundColor: this.state.selectedMachineType
                                ? "inherit"
                                : "rgba(0,0,0,0.06)",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: !this.state.selectedMachineType
                                ? "rgba(0,0,0,0.15)"
                                : undefined,
                            },
                          }}
                        />
                      )}
                      disabled={this.state.isLoading || !this.state.selectedMachineType}
                    />
                  </Grid>
                  {this.canShowLocalDisplayNumber() && (
                    <Grid item xs={12} md={6}>
                      <AppNumberInput
                        fullWidth
                        label="Nº en local"
                        value={this.state.localMachine.LocalDisplayNumber}
                        onValueChange={(value) =>
                          this.setState((prev) => ({
                            localMachine: {
                              ...prev.localMachine,
                              LocalDisplayNumber: value,
                            },
                            generalError: "",
                          }))
                        }
                        decimalScale={0}
                        allowNegative={false}
                        min={1}
                        error={Boolean(localDisplayNumberError)}
                        helperText={localDisplayNumberError}
                      />
                    </Grid>
                  )}
                </Grid>
              </AppFormSection>
            </Grid>

            {this.state.selectedMachine && (
            <Grid item xs={12}>
              <AppFormSection
                title="Configuración de la máquina"
                sx={mainSectionSx}
              >
                {this.state.isConfigurationLoading ? (
                  <Box role="status" sx={{ display: "flex", alignItems: "center", gap: 1.5, minHeight: 48, color: formPalette.mutedText, fontSize: "0.875rem" }}>
                    <CircularProgress size={18} />
                    Cargando configuración…
                  </Box>
                ) : (
                <>
                <Box>
                  <Typography id="local-machine-modality-label" variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Modalidad
                  </Typography>
                  <RadioGroup
                    name="local-machine-modality"
                    value={this.state.isRented ? "rented" : "standard"}
                    onChange={(event) => this.handleToggleRented(event.target.value === "rented")}
                    aria-labelledby="local-machine-modality-label"
                    sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}
                  >
                    <FormControlLabel
                      value="standard"
                      control={<Radio size="small" />}
                      label="Sin alquiler"
                      sx={quietChoiceSx(!this.state.isRented)}
                    />
                    <FormControlLabel
                      value="rented"
                      control={<Radio size="small" />}
                      label="Alquiler"
                      sx={quietChoiceSx(this.state.isRented)}
                    />
                  </RadioGroup>

                  {this.state.isRented && (
                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <AppNumberInput
                            fullWidth
                            label="Precio del alquiler"
                            required
                            name="price"
                            value={this.state.rentData.price}
                            onValueChange={(value) =>
                              this.setState((prev) => ({
                                rentData: {
                                  ...prev.rentData,
                                  price: value,
                                },
                              }))
                            }
                            decimalScale={2}
                            fixedDecimalScale={false}
                            allowNegative={false}
                            min={0}
                            error={this.state.showRequiredErrors && (!this.state.rentData.price || Number(this.state.rentData.price) <= 0)}
                            helperText={this.state.showRequiredErrors && (!this.state.rentData.price || Number(this.state.rentData.price) <= 0) ? "Ingresá un precio mayor que cero." : ""}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Frecuencia de pago"
                            required
                            name="paymentFrequency"
                            value={this.state.rentData.paymentFrequency}
                            onChange={this.handleRentDataChange}
                            error={this.state.showRequiredErrors && !this.state.rentData.paymentFrequency?.trim()}
                            helperText={this.state.showRequiredErrors && !this.state.rentData.paymentFrequency?.trim() ? "Ingresá una frecuencia de pago." : ""}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Box>

                {showFormulaSection && (
                  <Box sx={{ mt: 3 }}>
                    {isChipType ? (
                      hasExistingChipValue ? (
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Valor ficha existente $ {this.state.existingFormulaForType.Value}
                        </Typography>
                      ) : (
                        <Box>
                          <Typography
                            id="local-machine-chip-value-label"
                            variant="body2"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            Valor de ficha
                          </Typography>
                          <Box sx={{ width: "100%", minWidth: 0 }}>
                            <ButtonGroup
                              variant="outlined"
                              size="small"
                              fullWidth
                              aria-labelledby="local-machine-chip-value-label"
                              sx={compactChoiceGroupSx(6, this.state.showFormulaAdjust)}
                            >
                              {[50, 60, 80, 100, 120].map((value) => (
                                <Button
                                  key={value}
                                  variant={this.state.selectedFormulaPreset === `chip${value}` ? "contained" : "outlined"}
                                  aria-pressed={this.state.selectedFormulaPreset === `chip${value}`}
                                  onClick={() => this.handlePresetChipValue(value)}
                                >
                                  $ {value}
                                </Button>
                              ))}
                              <Button
                                variant={this.state.showFormulaAdjust ? "contained" : "outlined"}
                                aria-pressed={this.state.showFormulaAdjust}
                                onClick={this.handleToggleFormulaAdjust}
                              >
                                Ajustar
                              </Button>
                            </ButtonGroup>
                            {this.state.showFormulaAdjust && (
                              <>
                                <Box
                                  component="button"
                                  id="chip-value-trigger"
                                  type="button"
                                  aria-haspopup="menu"
                                  aria-expanded={Boolean(this.state.chipValueMenuAnchorEl)}
                                  aria-controls={this.state.chipValueMenuAnchorEl ? "chip-value-menu" : undefined}
                                  onClick={(event) => this.setState({ chipValueMenuAnchorEl: event.currentTarget })}
                                  sx={{
                                    ...profitAdjustCellSx,
                                    gridColumn: "auto",
                                    height: 40,
                                    mt: "-1px",
                                    px: 1,
                                    justifyContent: "space-between",
                                    border: chipValueError ? "2px solid #d32f2f" : `1px solid ${formPalette.softBorder}`,
                                    backgroundColor: formPalette.inset,
                                    color: formPalette.text,
                                    borderRadius: "0 0 4px 4px",
                                    cursor: "pointer",
                                    "&:hover": { backgroundColor: formPalette.selected },
                                    "&:focus-visible": { outline: `2px solid ${formPalette.accentHover}`, outlineOffset: -3 },
                                  }}
                                >
                                  <Box component="span" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    Valor ficha
                                    {this.state.formulaForm.Value !== "" ? ` $ ${this.state.formulaForm.Value}` : ""}
                                    <Box component="span" sx={{ ml: 0.5, color: "#d32f2f" }}>*</Box>
                                  </Box>
                                  <ArrowDropDownIcon fontSize="small" sx={{ ml: 0.5, flexShrink: 0 }} />
                                </Box>
                                <Menu
                                  id="chip-value-menu"
                                  anchorEl={this.state.chipValueMenuAnchorEl}
                                  open={Boolean(this.state.chipValueMenuAnchorEl)}
                                  onClose={() => this.setState({ chipValueMenuAnchorEl: null })}
                                  MenuListProps={{ "aria-labelledby": "chip-value-trigger" }}
                                  PaperProps={{
                                    sx: {
                                      color: formPalette.text,
                                      minWidth: this.state.chipValueMenuAnchorEl
                                        ? Math.max(this.state.chipValueMenuAnchorEl.offsetWidth / 2, 180)
                                        : 180,
                                      maxHeight: 320,
                                      "& .MuiMenuItem-root.Mui-selected": {
                                        backgroundColor: formPalette.selected,
                                        color: formPalette.text,
                                        fontWeight: 700,
                                      },
                                      "& .MuiMenuItem-root.Mui-selected:hover": {
                                        backgroundColor: formPalette.selectedHover,
                                      },
                                    },
                                  }}
                                >
                                  {Array.from({ length: 39 }, (_, index) => 10 + index * 5).map((value) => (
                                    <MenuItem
                                      key={value}
                                      selected={this.state.formulaForm.Value === String(value)}
                                      onClick={() => this.handleChipValueMenuSelect(value)}
                                    >
                                      $ {value}
                                    </MenuItem>
                                  ))}
                                </Menu>
                                {chipValueError && (
                                  <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                                    Seleccioná un valor de ficha.
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                        </Box>
                      )
                    ) : (
                      <Box sx={{ display: "grid", gap: 1 }}>
                        <RadioGroup
                          name="local-machine-count-mode"
                          value={this.state.localMachine.IsAccumulative ? "accumulative" : "reset"}
                          onChange={(event) =>
                            this.setState((prev) => ({
                              localMachine: {
                                ...prev.localMachine,
                                IsAccumulative: event.target.value === "accumulative",
                              },
                            }))
                          }
                          aria-label="Configuración de conteo"
                          sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}
                        >
                          <FormControlLabel
                            value="accumulative"
                            control={<Radio size="small" />}
                            label="Acumulativa"
                            sx={quietChoiceSx(this.state.localMachine.IsAccumulative)}
                          />
                          <FormControlLabel
                            value="reset"
                            control={<Radio size="small" />}
                            label="Con borrado"
                            sx={quietChoiceSx(!this.state.localMachine.IsAccumulative)}
                          />
                        </RadioGroup>

                        <Box>
                          <Typography
                            id="local-machine-formula-label"
                            variant="body2"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            Fórmula <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                          </Typography>
                          <ButtonGroup
                            variant="outlined"
                            size="small"
                            fullWidth
                            aria-labelledby="local-machine-formula-label"
                            sx={compactChoiceGroupSx(
                              6,
                              this.state.showFormulaAdjust,
                              this.state.showRequiredErrors &&
                                !this.state.selectedFormulaPreset &&
                                !this.state.showFormulaAdjust
                            )}
                          >
                            {[
                              { symbol: "/", value: 10 },
                              { symbol: "/", value: 5 },
                              { symbol: "*", value: 10 },
                              { symbol: "*", value: 5 },
                            ].map(({ symbol, value }) => (
                              <Button
                                key={`${symbol}${value}`}
                                variant={this.state.selectedFormulaPreset === `${symbol}${value}` ? "contained" : "outlined"}
                                aria-pressed={this.state.selectedFormulaPreset === `${symbol}${value}`}
                                onClick={() => this.handlePresetFormulaTragamoneda(symbol, value)}
                              >
                                {symbol}{value}
                              </Button>
                            ))}
                            <Button
                              variant={this.state.selectedFormulaPreset === "no-formula" ? "contained" : "outlined"}
                              aria-pressed={this.state.selectedFormulaPreset === "no-formula"}
                              aria-label="$; sin fórmula"
                              onClick={this.handlePresetNoFormula}
                            >
                              $
                            </Button>
                            <Button
                              variant={this.state.showFormulaAdjust ? "contained" : "outlined"}
                              aria-pressed={this.state.showFormulaAdjust}
                              onClick={this.handleToggleFormulaAdjust}
                            >
                              Ajustar
                            </Button>
                          </ButtonGroup>

                          {this.state.showFormulaAdjust && (
                            <Grid container spacing={2} sx={{ mt: 0 }}>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  label="Tipo"
                                  required
                                  select
                                  value={this.state.formulaForm.Type}
                                  onChange={(event) =>
                                    this.setState((prev) => ({
                                      formulaForm: { ...prev.formulaForm, Type: event.target.value },
                                    }))
                                  }
                                >
                                  <MenuItem value="/">/</MenuItem>
                                  <MenuItem value="*">*</MenuItem>
                                </TextField>
                              </Grid>
                              <Grid item xs={6}>
                                <AppNumberInput
                                  fullWidth
                                  label="Valor"
                                  required
                                  decimalScale={0}
                                  allowNegative={false}
                                  min={1}
                                  value={this.state.formulaForm.Value}
                                  error={this.state.showRequiredErrors && !this.state.formulaForm.Value}
                                  helperText={this.state.showRequiredErrors && !this.state.formulaForm.Value ? "Ingresá un valor." : ""}
                                  onValueChange={(value) =>
                                    this.setState((prev) => ({
                                      formulaForm: { ...prev.formulaForm, Value: value },
                                    }))
                                  }
                                />
                              </Grid>
                            </Grid>
                          )}

                          {this.state.showRequiredErrors &&
                            !this.state.selectedFormulaPreset &&
                            !this.state.showFormulaAdjust && (
                              <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                                Seleccioná una configuración.
                              </Typography>
                            )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
                </>
                )}

              {showCounterSection && (
                <AppFormSection
                  title="Contadores"
                  sx={counterSectionSx}
                >

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(this.state.counterConfig.enabled)}
                        onChange={(e) => this.handleCounterConfigEnabledChange(e.target.checked)}
                      />
                    }
                    label="Usar contadores"
                  />

                  {this.state.counterConfig.enabled && (
                    <Box sx={{ mt: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <RadioGroup
                            name="local-machine-counter-formulas"
                            value={this.state.counterConfig.useDifferentFormulas ? "different" : "same"}
                            onChange={(event) =>
                              this.handleCounterUseDifferentFormulasChange(event.target.value === "different")
                            }
                            aria-label="Configuración de fórmulas de contadores"
                            sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}
                          >
                            <FormControlLabel
                              value="same"
                              control={<Radio size="small" />}
                              label="Misma fórmula"
                              sx={quietChoiceSx(!this.state.counterConfig.useDifferentFormulas)}
                            />
                            <FormControlLabel
                              value="different"
                              control={<Radio size="small" />}
                              label="Fórmulas distintas"
                              sx={quietChoiceSx(this.state.counterConfig.useDifferentFormulas)}
                            />
                          </RadioGroup>
                        </Grid>

                        {!this.state.counterConfig.useDifferentFormulas && (
                          <Grid item xs={12}>
                            {this.renderCounterFormulaFields("shared", "Fórmula")}
                          </Grid>
                        )}

                        {this.state.counterConfig.useDifferentFormulas && (
                          <>
                            <Grid item xs={12} md={6}>
                              {this.renderCounterFormulaFields("in", "Fórmula entrada")}
                            </Grid>

                            <Grid item xs={12} md={6}>
                              {this.renderCounterFormulaFields("out", "Fórmula salida")}
                            </Grid>
                          </>
                        )}

                        <Grid item xs={12} md={6}>
                          <AppNumberInput
                            fullWidth
                            label="Tolerancia"
                            required
                            value={this.state.counterConfig.ToleranceAmount}
                            onValueChange={(value) =>
                              this.handleCounterConfigChange("ToleranceAmount", value)
                            }
                            decimalScale={2}
                            fixedDecimalScale={false}
                            allowNegative={false}
                            min={0}
                            error={this.state.showRequiredErrors && (this.state.counterConfig.ToleranceAmount === "" || Number(this.state.counterConfig.ToleranceAmount) < 0)}
                            helperText={this.state.showRequiredErrors && (this.state.counterConfig.ToleranceAmount === "" || Number(this.state.counterConfig.ToleranceAmount) < 0) ? "Ingresá una tolerancia mayor o igual a cero." : ""}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Tooltip title="Diferencia máxima aceptada entre la recaudación oficial y el contador." describeChild>
                                    <IconButton type="button" size="small" edge="end" aria-label="Información sobre la tolerancia">
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {this.state.counterConfigError && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      {this.state.counterConfigError}
                    </Typography>
                  )}
                </AppFormSection>
              )}
              </AppFormSection>
            </Grid>
            )}

            {showProfitSection && (
              <Grid item xs={12}>
                <AppFormSection
                  title={hasExistingProfitConfigured ? "Ganancias existentes" : "Ganancias · Cliente / Dueño"}
                  sx={mainSectionSx}
                >
                {hasExistingProfitConfigured ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr)",
                      alignItems: "center",
                    }}
                  >
                    <Tooltip describeChild title="Para editar los porcentajes, ir al menú en la pantalla del local">
                      <Box
                        tabIndex={0}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          width: "100%",
                          height: 36,
                          boxSizing: "border-box",
                          border: `1px solid ${formPalette.softBorder}`,
                          borderRadius: 1,
                          backgroundColor: formPalette.inset,
                          color: formPalette.text,
                          fontSize: selectorFontSize,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          cursor: "help",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                          Cliente {Math.round(Number(this.state.existingProfitPercentage.ClientPercentage || 0))}%
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", borderLeft: `1px solid ${formPalette.softBorder}` }}>
                          Dueño {Math.round(Number(this.state.existingProfitPercentage.OwnerPercentage || 0))}%
                        </Box>
                      </Box>
                    </Tooltip>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr)",
                      alignItems: "start",
                      rowGap: 1,
                    }}
                  >
                      <Box sx={{ width: "100%", minWidth: 0 }}>
                      <ButtonGroup
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{
                          ...quietSegmentedButtonSx,
                          display: "grid",
                          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                          "& .MuiButton-root": {
                            ...quietSegmentedButtonSx["& .MuiButton-root"],
                            width: "100%",
                            minWidth: 0,
                            boxSizing: "border-box",
                            height: 36,
                            px: { xs: 0.5, sm: 1 },
                          },
                          "& .MuiButtonGroup-middleButton, & .MuiButtonGroup-lastButton": {
                            marginLeft: 0,
                          },
                          "& .MuiButtonGroup-firstButton, & .MuiButtonGroup-middleButton": {
                            borderRightWidth: 0,
                          },
                          ...(this.state.showProfitPercentageAdjust && {
                            "& .MuiButtonGroup-firstButton": { borderBottomLeftRadius: 0 },
                            "& .MuiButtonGroup-lastButton": { borderBottomRightRadius: 0 },
                          }),
                        }}
                        aria-label="Ganancias · Cliente / Dueño"
                      >
                        <Button
                          variant={this.state.selectedProfitPreset === "50/50" ? "contained" : "outlined"}
                          aria-pressed={this.state.selectedProfitPreset === "50/50"}
                          onClick={() => this.handlePresetSplit(50, "50/50")}
                        >
                          50/50
                        </Button>
                        <Button
                          variant={this.state.selectedProfitPreset === "60/40" ? "contained" : "outlined"}
                          aria-pressed={this.state.selectedProfitPreset === "60/40"}
                          onClick={() => this.handlePresetSplit(60, "60/40")}
                        >
                          60/40
                        </Button>
                        <Button
                          variant={this.state.selectedProfitPreset === "100/0" ? "contained" : "outlined"}
                          aria-pressed={this.state.selectedProfitPreset === "100/0"}
                          onClick={() => this.handlePresetSplit(100, "100/0")}
                        >
                          100/0
                        </Button>
                        <Button
                          variant={isCustomProfitSelected ? "contained" : "outlined"}
                          aria-pressed={isCustomProfitSelected}
                          onClick={() =>
                            this.setState((prev) => ({
                              showProfitPercentageAdjust: !prev.showProfitPercentageAdjust,
                              selectedProfitPreset: prev.showProfitPercentageAdjust
                                ? prev.selectedProfitPreset
                                : null,
                              profitMenuAnchorEl: null,
                            }))
                          }
                        >
                          Ajustar
                        </Button>
                      </ButtonGroup>
                      {this.state.showProfitPercentageAdjust && (
                        <>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                              width: "100%",
                              height: 36,
                              mt: "-1px",
                            }}
                          >
                            <Box
                              component="button"
                              id="client-profit-trigger"
                              type="button"
                              aria-haspopup="menu"
                              aria-expanded={Boolean(this.state.profitMenuAnchorEl)}
                              aria-controls={this.state.profitMenuAnchorEl ? "client-profit-menu" : undefined}
                              onClick={(event) => this.setState({ profitMenuAnchorEl: event.currentTarget })}
                              sx={{
                                ...profitAdjustCellSx,
                                px: 1,
                                justifyContent: "space-between",
                                border: profitClientError ? "2px solid #d32f2f" : `1px solid ${formPalette.accent}`,
                                borderRightWidth: 0,
                                borderRadius: "0 0 0 4px",
                                cursor: "pointer",
                                "&:hover": { backgroundColor: formPalette.selectedHover },
                                "&:focus-visible": { outline: `2px solid ${formPalette.accentHover}`, outlineOffset: -3 },
                              }}
                            >
                              <Box component="span" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                Cliente
                                {this.state.localMachineProfitPercentage.clientPercentage !== ""
                                  ? ` ${this.state.localMachineProfitPercentage.clientPercentage}%`
                                  : ""}
                                <Box component="span" sx={{ ml: 0.5, color: "#d32f2f" }}>*</Box>
                              </Box>
                              <ArrowDropDownIcon fontSize="small" sx={{ ml: 0.5, flexShrink: 0 }} />
                            </Box>
                            <Box
                              component="output"
                              aria-live="polite"
                              sx={{
                                ...profitAdjustCellSx,
                                justifyContent: "center",
                                borderLeft: profitClientError ? "2px solid #d32f2f" : `1px solid ${formPalette.accent}`,
                                borderRadius: "0 0 4px 0",
                              }}
                            >
                              Dueño {this.state.localMachineProfitPercentage.ownerPercentage === ""
                                ? "—"
                                : `${this.state.localMachineProfitPercentage.ownerPercentage}%`}
                            </Box>
                          </Box>
                          <Menu
                            id="client-profit-menu"
                            anchorEl={this.state.profitMenuAnchorEl}
                            open={Boolean(this.state.profitMenuAnchorEl)}
                            onClose={() => this.setState({ profitMenuAnchorEl: null })}
                            MenuListProps={{ "aria-labelledby": "client-profit-trigger" }}
                            PaperProps={{
                              sx: {
                                color: formPalette.text,
                                minWidth: this.state.profitMenuAnchorEl?.offsetWidth || 160,
                                maxHeight: 320,
                                "& .MuiMenuItem-root.Mui-selected": {
                                  backgroundColor: formPalette.selected,
                                  color: formPalette.text,
                                  fontWeight: 700,
                                },
                                "& .MuiMenuItem-root.Mui-selected:hover": {
                                  backgroundColor: formPalette.selectedHover,
                                },
                              },
                            }}
                          >
                            {Array.from({ length: 11 }, (_, index) => index * 10).map((percentage) => (
                              <MenuItem
                                key={percentage}
                                selected={this.state.localMachineProfitPercentage.clientPercentage === String(percentage)}
                                onClick={() => this.handleProfitMenuSelect(percentage)}
                              >
                                {percentage}%
                              </MenuItem>
                            ))}
                          </Menu>
                          {profitClientError && (
                            <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                              Seleccioná un porcentaje.
                            </Typography>
                          )}
                        </>
                      )}
                      </Box>
                    {profitClientError && !this.state.showProfitPercentageAdjust && (
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{ gridColumn: 1 }}
                      >
                        Seleccioná un reparto de ganancias o ingresá los porcentajes.
                      </Typography>
                    )}
                  </Box>
                )}
                </AppFormSection>
              </Grid>
            )}

            {this.state.selectedMachine && !this.state.isConfigurationLoading && (
            <Grid item xs={12}>
              <AppFormSection title="Ingreso al local" sx={mainSectionSx}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
                  <DatePicker
                    label="Fecha de ingreso al local"
                    value={effectiveDate}
                    onChange={this.handleEffectiveDateChange}
                    minDate={minEffectiveDate || undefined}
                    maxDate={today}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: Boolean(effectiveDateError),
                        helperText: effectiveDateError,
                      },
                    }}
                  />
                </LocalizationProvider>
              </AppFormSection>
            </Grid>
            )}

            {generalError && (
              <Grid item xs={12}>
                <Typography variant="body2" color="error">
                  {generalError}
                </Typography>
              </Grid>
            )}

          </Grid>
          </Box>

          {/* ===================== BOTONES FINALES ===================== */}
          <Box sx={{ flexShrink: 0, backgroundColor: "#fff", borderTop: "1px solid #d0dbe6", pt: 2, pb: { xs: 2, sm: 2.5 } }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <CancelButton
                    onClick={this.handleCancel}
                    fullWidth
                  >
                    Cancelar
                  </CancelButton>
                </Grid>

                <Grid item xs={12} md={6}>
                  <AcceptButton
                    type="submit"
                    disabled={this.state.isLoading || this.state.isConfigurationLoading || this.state.isSubmitting}
                    startIcon={this.state.isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                    fullWidth
                  >
                    {this.state.isSubmitting ? "Agregando…" : "Agregar máquina"}
                  </AcceptButton>
                </Grid>
              </Grid>
          </Box>
        </form>
      </Paper>
    );
  }
}

CreateLocalMachineForm.propTypes = {
  closeForm: propTypes.func.isRequired,
  local: propTypes.object.isRequired,
};

export default CreateLocalMachineForm;
